import React, { useState } from "react";
import { X, CalendarClock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const VisitScheduler = ({ property, onClose }) => {
  const { user } = useAuth();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const ownerWa = (property.contact_whatsapp || property.contact_phone || "").replace(/[^0-9]/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Choisissez une date et une heure"); return; }
    setLoading(true);
    try {
      await api.post(`/properties/${property.id}/visits`, { date, time, name, phone, message });
      toast.success("Demande de visite envoyée ! Le propriétaire a été notifié.", { duration: 6000 });
      if (ownerWa) {
        const waText = encodeURIComponent(
          `Bonjour, je souhaite planifier une visite pour "${property.title}" (${property.neighborhood}, ${property.city}) le ${date} à ${time}.\n\nJe suis ${name} (${phone}).${message ? "\n\n" + message : ""}\n\n— via IMORA Tchad`
        );
        window.open(`https://wa.me/${ownerWa}?text=${waText}`, "_blank", "noopener");
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="visit-modal" className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-black text-xl tracking-tighter flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[#FF6B1A]" /> Planifier une visite
          </h3>
          <button onClick={onClose} data-testid="visit-close-btn" className="p-1 hover:bg-neutral-100 rounded-full"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-neutral-500 mb-4 line-clamp-1">{property.title}</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="imora-label">Date de visite *</label>
            <input type="date" required min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="visit-date-input" className="imora-input" />
          </div>
          <div>
            <label className="imora-label">Heure *</label>
            <div className="grid grid-cols-5 gap-1.5">
              {TIME_SLOTS.map((tSlot) => (
                <button
                  key={tSlot}
                  type="button"
                  onClick={() => setTime(tSlot)}
                  data-testid={`visit-time-${tSlot.replace(":", "")}`}
                  className={`text-xs font-bold py-2 rounded-lg border transition ${time === tSlot ? "bg-[#FF6B1A] text-white border-[#FF6B1A]" : "bg-white border-neutral-200 hover:border-[#FF6B1A]"}`}
                >
                  {tSlot}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="imora-label">Votre nom *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="visit-name-input" className="imora-input" />
          </div>
          <div>
            <label className="imora-label">Votre téléphone *</label>
            <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+235 ..." data-testid="visit-phone-input" className="imora-input" />
          </div>
          <div>
            <label className="imora-label">Message (optionnel)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="visit-message-input" className="imora-input min-h-[60px] py-2" />
          </div>
          <button type="submit" disabled={loading} data-testid="visit-submit-btn" className="imora-btn-primary w-full disabled:opacity-60">
            {loading ? "Envoi…" : "Envoyer la demande"}
          </button>
          {ownerWa && (
            <p className="text-xs text-neutral-400 text-center flex items-center justify-center gap-1">
              <MessageCircle className="h-3 w-3" /> WhatsApp du propriétaire s'ouvrira avec votre demande pré-remplie
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default VisitScheduler;
